import React, { useState } from 'react';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import { Select, Option, Value } from 'baseui/select';
import { Textarea } from 'baseui/textarea';
import { COUNTRIES, PhoneInput } from 'baseui/phone-input';
import { Block } from 'baseui/block';
import { Cell } from 'baseui/layout-grid';
import { RadioGroup, Radio } from 'baseui/radio';
import CustomFlag from './custom-flag';
import SalaryTabs from './salary-tabs';

export interface FormData {
    name: string;
    email: string;
    phone: string;
    countryId: Option[];
    city: string;
    developerType: Option[];
    salaryCurrency: string;
    salaryFrequency: string;
    expectedSalary: string;
    yearsOfExperience: string;
    noticePeriod: string;
    note: string;
}

interface FormControlsProps {
    formData: FormData;
    countries: Option[];
    phoneInputCountries: string[];
    errors: Record<string, string>;
    handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    handleSelectChange: (name: keyof FormData, value: Value) => void;
    currency: string;
    phoneCountry: Option;
    engagementTypes: Option[];
    setPhoneCountry: (option: Option) => void;
}

const FormControls: React.FC<FormControlsProps> = ({
    formData,
    countries,
    phoneInputCountries,
    errors,
    phoneCountry,
    setPhoneCountry,
    handleChange,
    handleSelectChange,
    currency,
    engagementTypes
}) => {
    const filteredCountries = Object.keys(COUNTRIES)
        .filter((code) => phoneInputCountries.includes(code))
        .map((code) => ({
            id: code,
            // label: <span> <CustomFlag $iso={code} />({COUNTRIES[code].dialCode}) {COUNTRIES[code].label}</span>,
            dialCode: COUNTRIES[code].dialCode,
            country: COUNTRIES[code],
        }));

    const getLabel = ({ option }) => {
        if (!option) return null;
        const country = COUNTRIES[option.id];

        return (
            <React.Fragment>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CustomFlag $iso={option.id} />
                    <span>{country.label}</span>
                    <span style={{ color: 'gray', marginLeft: 'auto' }}>{country.dialCode}</span>
                </div>
            </React.Fragment>
        );
    };

    const getValueLabel = ({ option }) => {
        if (!option) return null;
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',  // ✅ Restrict width
                height: '40px',
                paddingLeft: '5px',
            }}>
                <CustomFlag $iso={option.id} />
            </div>
        );
    };

    const CustomCountrySelect = ({ value, onChange }) => {
        const [selected, setSelected] = useState(value);

        return (
            <Select
                options={filteredCountries}
                value={[selected]}
                clearable={false}
                filterOptions={(options, inputValue) =>
                    options.filter((option) =>
                        option.country.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                        option.dialCode.includes(inputValue)
                    )
                }
                onChange={(params) => {
                    if (params.value.length > 0) {
                        setSelected(params.value[0]);
                        onChange({ option: params.value[0].country });
                    }
                }}
                getOptionLabel={getLabel}
                getValueLabel={getValueLabel}
                overrides={{
                    ControlContainer: {
                        style: {
                            width: '100px',
                            padding: '0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRight: 'none',
                            position: 'relative',
                            height: '100%',
                        },
                    },
                    ValueContainer: {
                        style: {
                            padding: '0',
                            width: '100px',
                            height: '100%'
                        },
                    },
                    Dropdown: {
                        style: { maxHeight: '300px', minWidth: '400px', overflowY: 'auto' },
                    },
                    Root: {
                        style: { marginRight: '10px', borderRight: 'none', maxWidth: '100px' }
                    },
                }}
            />
        );
    };

    return (
        <>
            <Cell span={[6, 12]}>
                <FormControl label="">
                    <span />
                </FormControl>
            </Cell>

            <Cell span={[12, 6]}>
                <FormControl label="Name" error={errors.name}>
                    <Input
                        name="name"
                        placeholder="Enter candidate's name"
                        value={formData.name}
                        onChange={handleChange}
                    />
                </FormControl>
            </Cell>

            <Cell span={[12, 6]}>
                <FormControl label="Email" error={errors.email}>
                    <Input
                        name="email"
                        placeholder="Enter candidate's email"
                        value={formData.email}
                        type="email"
                        onChange={handleChange}
                    />
                </FormControl>
            </Cell>

            <Cell span={[12, 6]}>
                <FormControl label="Country" error={errors.countryId}>
                    <Select
                        options={countries.map((country) => ({
                            label: country.name,
                            id: country.id,
                            currency: country.currency
                        }))}
                        value={formData.countryId}
                        onChange={(params) => handleSelectChange('countryId', params.value)}
                        placeholder="Select a country"
                    />
                </FormControl>
            </Cell>

            <Cell span={[12, 6]}>
                <FormControl label="City (Optional)">
                    <Input
                        name="city"
                        placeholder="Enter city"
                        value={formData.city}
                        onChange={handleChange}
                    />
                </FormControl>
            </Cell>

            <Cell span={[12, 6]}>
                <FormControl label="Mobile Phone Number" error={errors.phone}>
                    <PhoneInput
                        countries={phoneInputCountries}
                        // @ts-ignore
                        country={phoneCountry}

                        onCountryChange={({ option }) => {
                            // @ts-ignore
                            const numericValue = `${option.dialCode}${formData.phone.replace(phoneCountry.dialCode, '').replace(/\D/g, '')}`;
                            // Update the country and reapply the dial code with the current number
                            // @ts-ignore
                            setPhoneCountry(option);
                            // @ts-ignore
                            handleChange({ target: { name: 'phone', value: numericValue } })
                        }}
                        text={formData.phone.replace(phoneCountry.dialCode, '')}
                        // @ts-ignore
                        onTextChange={(e) => {
                            const phoneNumber = `${phoneCountry.dialCode}${e.target.value.replace(/\D/g, '')}`;
                            // @ts-ignore
                            handleChange({ ...e, target: { name: 'phone', value: phoneNumber } })
                        }}
                        overrides={{
                            FlagContainer: {
                                component: CustomFlag,
                            },
                            CountrySelect: {
                                component: CustomCountrySelect,
                                props: {
                                    value: phoneCountry,
                                    onChange: ({ option }) => setPhoneCountry(option),
                                },
                            },
                        }}
                    />
                </FormControl>
            </Cell>

            <Cell span={[12, 6]}>
                <FormControl label="">
                    <span />
                </FormControl>
            </Cell>

            <Cell span={[12, 6]}>
                <FormControl label="Type of Engagement with Turing  " error={errors.developerType}>
                    <Select
                        options={engagementTypes}
                        value={formData.developerType}
                        onChange={(params) => handleSelectChange('developerType', params.value)}
                        placeholder="Select type of developer"
                    />
                </FormControl>
            </Cell>

            <Cell span={[12, 6]}>
                <FormControl label="Years of Experience" error={errors.yearsOfExperience}>
                    <Input
                        type="number"
                        name="yearsOfExperience"
                        placeholder="Enter years of experience"
                        value={formData.yearsOfExperience}
                        onChange={(e) => {
                            let { name, value } = e.target;

                            if (value.includes("e") || value.includes("-")) {
                                return;
                            }
                            value = value.replace(/\D/g, "");
                            const number = Number(value);

                            // Ensure value is not NaN and is less than or equal to 50
                            if (!Number.isNaN(number) && number <= 50) {
                                handleChange({
                                    ...e,
                                    target: {
                                        ...e.target,
                                        name,
                                        value: String(number),
                                    },
                                });
                            }
                        }}
                        max={50}
                    />
                </FormControl>
            </Cell>

            <Cell span={[12, 6]}>
                <FormControl label="Notice Period">
                    <RadioGroup
                        name="noticePeriod"
                        value={formData.noticePeriod}
                        onChange={handleChange}
                    >
                        <Radio value="0">Immediate</Radio>
                        <Radio value={formData.noticePeriod !== '0' ? formData.noticePeriod : ''}>
                            <Block display="flex" alignItems="center">
                                <Input
                                    name="noticePeriod"
                                    placeholder="2"
                                    value={formData.noticePeriod !== '0' ? formData.noticePeriod : ''}
                                    onChange={handleChange}
                                    type="number"
                                    min={1}
                                    max={52}
                                    overrides={{
                                        Root: {
                                            style: { width: '80px', marginRight: '8px' },
                                        },
                                    }}
                                />
                                weeks
                            </Block>
                        </Radio>
                    </RadioGroup>
                </FormControl>
            </Cell>


            <SalaryTabs
                frequency={formData.salaryFrequency}
                handleChange={handleChange}
            />

            <Cell span={[12, 6]}>
                <FormControl label={`Expected Salary (${formData.salaryFrequency === 'anually' ? 'Anually' : 'Hourly'})`} error={errors.expectedSalary}>
                    <Input
                        type="number"
                        name="expectedSalary"
                        placeholder={`Enter expected salary`}
                        value={formData.expectedSalary}
                        onChange={handleChange}
                        endEnhancer={() => (
                            <span style={{ display: 'flex', alignItems: 'center', padding: '0 8px' }}>
                                {currency} {formData.salaryFrequency === 'annually' ? '/yr' : '/hr'}
                            </span>
                        )}
                        overrides={{
                            InputContainer: {
                                style: {
                                    backgroundColor: '#f5f5f5',
                                },
                            },
                            Input: {
                                style: {
                                    paddingRight: '100px',
                                },
                            },
                            EndEnhancer: {
                                style: {
                                    width: '100px',
                                    backgroundColor: 'transparent',
                                    paddingRight: '8px',
                                },
                            },
                        }}
                    />
                </FormControl>
            </Cell>

            <Cell span={[12, 6]}>
                <FormControl label="Notes">
                    <Textarea
                        name="note"
                        placeholder="Add skills, and any other information about the developer"
                        value={formData.note}
                        onChange={handleChange}
                    />
                </FormControl>
            </Cell>
        </>
    )
};

export default FormControls;